import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { GetSecondOpinionsForPatient } from '@virtual-care/client';
import {
  formatSecondOpinionDueDate,
  getLocalizedSpeciality,
} from '@virtual-care/isomorphic';
import { ReservationStatus } from '@virtual-care/ui';

import { useLanguage } from '../../../i18n/use-language.ts';

export function SecondOpinionCard({
  record,
}: {
  record: GetSecondOpinionsForPatient['records'][number];
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const speciality = getLocalizedSpeciality(record.speciality, language);
  const navigate = useNavigate();

  const doctorName = record.doctor
    ? `${record.doctor.firstName} ${record.doctor.lastName}`
    : undefined;

  return (
    <div
      className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      onClick={() => navigate(`/second-opinion/${record.id}`)}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-chart-2 flex h-6 w-6 items-center justify-center rounded-full">
            <FileText className="h-4 text-white" />
          </div>
          <div className="flex flex-col items-start gap-1">
            <span className="font-bold text-black">
              {t('cards.secondOpinion.prefix')} {record.counter}#
            </span>
            <ReservationStatus.Root status={record.status}>
              <ReservationStatus.Indicator />
              <ReservationStatus.Label render={(meta) => t(meta.key)} />
            </ReservationStatus.Root>
          </div>
        </div>
      </div>
      <div className="space-y-3 text-sm">
        <p>
          <span className="text-muted-foreground">
            {t('cards.secondOpinion.specialty')}:
          </span>{' '}
          <span className="font-semibold">{speciality.name}</span>
        </p>
        <p>
          <span className="text-muted-foreground">
            {t('cards.secondOpinion.deliveryTime')}:
          </span>{' '}
          <span className="font-semibold">
            {formatSecondOpinionDueDate(record)}
          </span>
        </p>
        <p>
          <span className="text-muted-foreground">
            {t('cards.secondOpinion.priority')}:
          </span>{' '}
          <span className="font-semibold">
            {record.urgent ? t('common.urgent') : t('common.normal')}
          </span>
        </p>
        {doctorName && (
          <p>
            <span className="text-muted-foreground">
              {t('cards.secondOpinion.doctorName')}:
            </span>{' '}
            <span className="font-semibold">{doctorName}</span>
          </p>
        )}
      </div>
    </div>
  );
}
